import { get, post, put } from '@/api/lib/amplify-wrapper'
import {
  OneOnOneMeeting,
  CreateMeetingRequest,
  OneOnOneMeetingsListResponse
} from '@/types/performance'
import { createImpersonatedApiHeaders } from '@/lib/services/api-utils'

const API_NAME = 'performance'

/**
 * Helper function to serialize data for API requests
 * Converts Date objects to ISO strings for JSON serialization
 */
const serializeData = (data: any) => {
  return JSON.parse(JSON.stringify(data, (_key, value) => {
    // Convert Date objects to ISO strings
    if (value instanceof Date) {
      return value.toISOString()
    }
    return value
  }))
}

/**
 * Helper function to extract pagination metadata from response headers
 */
const extractPaginationMetadata = (headers: any, itemsLength: number): { nextToken?: string; itemCount: number } => {
  const nextToken = headers?.['x-next-token'] || headers?.['X-Next-Token']
  const itemCount = headers?.['x-item-count'] || headers?.['X-Item-Count']

  return {
    nextToken: nextToken || undefined,
    itemCount: itemCount ? parseInt(itemCount) : itemsLength
  }
}

/**
 * Map API errors to user-friendly messages
 */
const mapApiError = (error: any): string => {
  if (error.response?.status === 400) return 'Invalid input. Please check your data.'
  if (error.response?.status === 403) return 'You do not have permission to perform this action.'
  if (error.response?.status === 404) return 'Resource not found.'
  if (error.response?.status === 500) return 'Server error. Please try again later.'
  return 'An unexpected error occurred. Please try again.'
}

/**
 * Validate date format (YYYY-MM-DD)
 */
const validateDateFormat = (date: string): boolean => {
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/
  return dateRegex.test(date)
}

/**
 * Validate time format (HH:MM)
 */
const validateTimeFormat = (time: string): boolean => {
  const timeRegex = /^([01]\d|2[0-3]):([0-5]\d)$/
  return timeRegex.test(time)
}

// ============================================================================
// One-on-One Meetings API Functions
// ============================================================================

/**
 * List current user's 1-on-1 meetings with pagination and filtering
 *
 * Returns paginated list of 1-on-1 meetings where the current user is either
 * the manager or employee. This endpoint follows the /me self-service pattern.
 *
 * @param params - Optional filtering and pagination parameters
 * @param params.status - Filter by meeting status (scheduled, confirmed, completed, cancelled)
 * @param params.limit - Maximum number of items to return (default: 20)
 * @param params.nextToken - Token for fetching next page
 * @returns Promise resolving to paginated list of meetings
 * @throws Error with user-friendly message if API call fails
 */
export const listMeetingsMe = async (params?: {
  status?: 'scheduled' | 'confirmed' | 'completed' | 'cancelled'
  limit?: number
  nextToken?: string
}): Promise<OneOnOneMeetingsListResponse> => {
  try {
    const queryParams = new URLSearchParams()

    if (params?.status) queryParams.append('status', params.status)
    if (params?.limit) queryParams.append('limit', params.limit.toString())
    if (params?.nextToken) queryParams.append('nextToken', params.nextToken)

    const path = '/performance/one-on-ones/me' + (queryParams.toString() ? `?${queryParams.toString()}` : '')

    const restOperation = get({
      apiName: API_NAME,
      path,
      options: {
        headers: createImpersonatedApiHeaders()
      }
    })

    const response = await restOperation.response
    const data = await response.body.json()
    const items = data as unknown as OneOnOneMeeting[]

    const pagination = extractPaginationMetadata(response.headers, items.length)

    return {
      items,
      pagination
    }
  } catch (error: any) {
    console.error('Error listing 1-on-1 meetings:', error)
    const message = mapApiError(error)
    throw new Error(message)
  }
}

/**
 * Create a new 1-on-1 meeting
 *
 * Creates a new 1-on-1 meeting between the current user (as manager) and an employee.
 * Validates date and time formats before submission.
 *
 * @param data - Meeting creation data
 * @returns Promise resolving to the created OneOnOneMeeting
 * @throws Error with user-friendly message if API call fails or validation fails
 */
export const createMeeting = async (data: CreateMeetingRequest): Promise<OneOnOneMeeting> => {
  try {
    // Client-side validation
    if (!validateDateFormat(data.date)) {
      throw new Error('Invalid date format. Please use YYYY-MM-DD format.')
    }

    if (!validateTimeFormat(data.time)) {
      throw new Error('Invalid time format. Please use HH:MM format (24-hour).')
    }

    const path = '/performance/one-on-ones'

    const restOperation = post({
      apiName: API_NAME,
      path,
      options: {
        body: serializeData(data),
        headers: createImpersonatedApiHeaders()
      }
    })

    const response = await restOperation.response
    const responseData = await response.body.json()
    return responseData as unknown as OneOnOneMeeting
  } catch (error: any) {
    console.error('Error creating 1-on-1 meeting:', error)
    const message = mapApiError(error)
    throw new Error(message)
  }
}

/**
 * Update an existing 1-on-1 meeting
 *
 * Updates meeting details such as date, time, status, or notes.
 * Only the manager or hr-admin can update meetings.
 *
 * @param meetingId - The unique identifier of the meeting to update
 * @param updates - Partial meeting data to update
 * @returns Promise resolving to the updated OneOnOneMeeting
 * @throws Error with user-friendly message if API call fails
 */
export const updateMeeting = async (
  meetingId: string,
  updates: Partial<OneOnOneMeeting>
): Promise<OneOnOneMeeting> => {
  try {
    // Validate date/time if provided
    if (updates.date && !validateDateFormat(updates.date)) {
      throw new Error('Invalid date format. Please use YYYY-MM-DD format.')
    }

    if (updates.time && !validateTimeFormat(updates.time)) {
      throw new Error('Invalid time format. Please use HH:MM format (24-hour).')
    }

    const path = `/performance/one-on-ones/${meetingId}`

    const restOperation = put({
      apiName: API_NAME,
      path,
      options: {
        body: serializeData(updates),
        headers: createImpersonatedApiHeaders()
      }
    })

    const response = await restOperation.response
    const data = await response.body.json()
    return data as unknown as OneOnOneMeeting
  } catch (error: any) {
    console.error(`Error updating 1-on-1 meeting ${meetingId}:`, error)
    const message = mapApiError(error)
    throw new Error(message)
  }
}

/**
 * Cancel a 1-on-1 meeting
 *
 * Sets the meeting status to 'cancelled'. Only the manager or hr-admin can cancel meetings.
 *
 * @param meetingId - The unique identifier of the meeting to cancel
 * @returns Promise resolving when cancellation is complete (204 No Content)
 * @throws Error with user-friendly message if API call fails
 */
export const cancelMeeting = async (meetingId: string): Promise<void> => {
  try {
    const path = `/performance/one-on-ones/${meetingId}/cancel`

    const restOperation = post({
      apiName: API_NAME,
      path,
      options: {
        headers: createImpersonatedApiHeaders()
      }
    })

    await restOperation.response
    // 204 No Content - no response body
  } catch (error: any) {
    console.error(`Error cancelling 1-on-1 meeting ${meetingId}:`, error)
    const message = mapApiError(error)
    throw new Error(message)
  }
}

/**
 * Sync meeting with external calendar (Google Calendar or Outlook)
 *
 * Integrates the 1-on-1 meeting with an external calendar service using OAuth token.
 * The backend will create/update a calendar event and return the calendarEventId.
 *
 * @param meetingId - The unique identifier of the meeting to sync
 * @param calendarType - Calendar service type ('google' or 'outlook')
 * @param oauthToken - OAuth access token from calendar provider
 * @returns Promise resolving to updated meeting with calendarEventId
 * @throws Error with user-friendly message if API call fails
 */
export const syncCalendar = async (
  meetingId: string,
  calendarType: 'google' | 'outlook',
  oauthToken: string
): Promise<OneOnOneMeeting> => {
  try {
    const path = `/performance/one-on-ones/${meetingId}/calendar-sync`

    const restOperation = post({
      apiName: API_NAME,
      path,
      options: {
        body: {
          calendarType,
          oauthToken
        },
        headers: createImpersonatedApiHeaders()
      }
    })

    const response = await restOperation.response
    const data = await response.body.json()
    return data as unknown as OneOnOneMeeting
  } catch (error: any) {
    console.error(`Error syncing calendar for meeting ${meetingId}:`, error)
    const message = mapApiError(error)
    throw new Error(message)
  }
}
